/**
 * V2CanvasClipboardService - System clipboard wrapper for V2 StepNode copy/paste
 *
 * Uses the Browser Clipboard API (navigator.clipboard) to enable:
 * - Copy/paste within the same V2 canvas
 * - Copy/paste between V2 canvases in different browser tabs
 *
 * StepNodes are serialized to JSON with a V2-specific marker prefix. The
 * marker is deliberately distinct from V1 so V1 and V2 clipboards do NOT
 * interoperate — pasting a V1 copy into a V2 canvas is a silent no-op
 * (and vice versa), matching the schema boundary between V1 `Screen` and
 * V2 `StepNode`.
 *
 * Mirrors the V1 `CanvasClipboardService` design; only the payload shape
 * and marker differ.
 */

import type { FunnelVariable, StepNode } from '@segmently/schema-models/v2';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { StrategyScreenTransplantSource } from '@modules/strategy-blocks/utils/strategyScreenTransplant';

/**
 * Marker prefix for fast identification of Segmently V2 clipboard data.
 * Allows skipping full JSON parse when clipboard contains unrelated text
 * or a V1 payload.
 */
const CLIPBOARD_MARKER = '{"__segmently_v2_screens__":true';

/**
 * Serialized clipboard payload stored in system clipboard as JSON string.
 */
export interface V2ClipboardPayload {
  /** Marker for identification — always true */
  __segmently_v2_screens__: true;
  /** Optional payload version. Missing means legacy screen-only payload. */
  clipboardVersion?: 2;
  /** Deep-cloned V2 StepNode data */
  steps: StepNode[];
  /** Original step IDs (for building old→new ID mapping on paste) */
  originalStepIds: string[];
  /** Variable definitions needed by the copied steps. Optional for legacy compatibility. */
  variables?: Record<string, FunnelVariable>;
  /** Ordered variable dependency IDs included in `variables`. */
  variableDependencyIds?: string[];
  /** Non-blocking warnings discovered while building the clipboard payload. */
  warnings?: string[];
  /** Source metadata for diagnostics and opt-in transplant paste policy. */
  source?: {
    projectId?: string;
    onboardingId?: string;
    versionId?: string;
    strategyTransplant?: StrategyScreenTransplantSource;
  };
  /** Timestamp when copy was performed */
  copiedAt: number;
}

export interface V2CanvasClipboardCopyOptions {
  variables?: Record<string, FunnelVariable>;
  variableDependencyIds?: string[];
  warnings?: string[];
  source?: V2ClipboardPayload['source'];
}

/**
 * Service for copying/pasting V2 StepNodes via the system clipboard.
 * Works across browser tabs because it uses navigator.clipboard.
 */
export class V2CanvasClipboardService {
  /**
   * Copy V2 steps to the system clipboard as JSON.
   *
   * Steps are deep-cloned via `JSON.parse(JSON.stringify(...))` so
   * subsequent mutations of the source `FlowStepNode` objects do not
   * reach the clipboard.
   *
   * If the browser denies clipboard access (e.g. insecure origin,
   * Safari without a user gesture), the error is logged and swallowed
   * — copy becomes a no-op rather than propagating a rejection to the
   * canvas keyboard handler.
   */
  async copy(
    steps: Array<FlowStepNode>,
    options: V2CanvasClipboardCopyOptions = {},
  ): Promise<void> {
    const payload: V2ClipboardPayload = {
      __segmently_v2_screens__: true,
      clipboardVersion: 2,
      steps: steps.map(s => JSON.parse(JSON.stringify(s))),
      originalStepIds: steps.map(s => s.entityId),
      copiedAt: Date.now(),
    };

    if (options.variables && Object.keys(options.variables).length > 0) {
      payload.variables = JSON.parse(JSON.stringify(options.variables));
      payload.variableDependencyIds = [...(options.variableDependencyIds || Object.keys(options.variables))];
    }

    if (options.warnings && options.warnings.length > 0) {
      payload.warnings = [...options.warnings];
    }

    if (options.source) {
      payload.source = { ...options.source };
    }

    try {
      await navigator.clipboard.writeText(JSON.stringify(payload));
    } catch (err) {
      console.warn('[V2CanvasClipboardService] Copy failed:', err);
    }
  }

  /**
   * Read V2 steps from the system clipboard.
   *
   * Returns `null` in all of the following cases:
   * - Clipboard is empty
   * - Clipboard contains text that does not start with the V2 marker
   *   (including V1 Segmently payloads with `__segmently_screens__`)
   * - Clipboard contains marker-prefixed but invalid JSON
   * - Browser denies `readText` permission
   *
   * Callers treat `null` as a silent no-op — the paste handler simply
   * does nothing, matching V1 behavior for non-Segmently clipboard data.
   */
  async read(): Promise<V2ClipboardPayload | null> {
    try {
      const text = await navigator.clipboard.readText();

      // Fast check: if text doesn't start with our marker, skip full parse.
      // V1 payloads start with `{"__segmently_screens__":true` — different
      // prefix, so this check rejects them without a JSON.parse.
      if (!text.startsWith(CLIPBOARD_MARKER)) {
        return null;
      }

      const parsed = JSON.parse(text);
      if (!parsed.__segmently_v2_screens__ || !Array.isArray(parsed.steps)) {
        return null;
      }

      return parsed as V2ClipboardPayload;
    } catch {
      // Permission denied, empty clipboard, or invalid JSON — silent no-op
      return null;
    }
  }
}

/** Singleton instance */
export const v2CanvasClipboardService = new V2CanvasClipboardService();
